import {formatCents, useAsync2} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import React, {useContext, useMemo} from "react";
import {ChangePreviewContext} from "../ShopDrawingChangePreview";
import {
    AdjustCol,
    CentsCol,
    centsParseValue, CheckboxCol,
    Column,
    lookupNestedProp,
    NumberCol, RenderStringCol, sortNumericInner, sortStringInner,
    StringCol,
    ViewOnly
} from "../../../../misc/scroller/Table";
import {cellInActive} from "./cellconst";
import {PreviousValuePopup} from "../openings/PreviousValuePopup";
import {PriceLine2} from "./Frames";
import {Checkbox2Col} from "../ShopTableCol";
import {User} from "../../../../api/Users";
import {StatusCol} from "../openings/Openings";
import {isNullOrUndefined, MarkupEditCell, parseMarkup} from "./Pricing";
import {hasPermission} from "../../../../misc/Permission";
import {ChangesetContext} from "../changeset/ChangesetContext";
import {LinearProgress} from "@mui/material";
import {costEachColumnName, markupColumnName} from "./Const";
import {PriceRider} from "../../../../api/Pricing";
import {SidePricingPanelOnFocus} from "./SidePricingPanel";
import {green, grey, red} from "@mui/material/colors";
import {CellWithBg} from "../../../logistics/purchasing/CellWithBg";
import {CheckboxExtSelectorCol} from "../../manage/sov/CheckboxExtProvider";
import {MiscPrice} from "../../../../api/MiscPricing";
import {useAlternative} from "../../quote/alternative/Alternative";

export function usePricingKit2(project: number) {
    const alt = useAlternative();

    const markupReq = useAsync2((input) => {
        return api.pricing.getProjectMarkups(input)
    }, {
        project: project,
        alternative: alt,
    }, [project, alt]);

    const markup = markupReq.result;

    const ctx = useContext(ChangesetContext);
    const previewChangeset = useContext(ChangePreviewContext);
    const changeset = !!previewChangeset.sessionId || !!ctx.sessionId;

    const cellCustomize = useMemo(() => {
        return {
            backgroundColor: (row: PriceLine2, c: Column<PriceLine2>) => {
                if(row.refunded) {
                    return red["100"];
                }

                if(!changeset) return null;

                let changed = false;
                if("isChangedSinceBackup" in c && c.isChangedSinceBackup) {
                    changed = c.isChangedSinceBackup(row)
                } else if(c.editKey) {
                    changed = !!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)
                } else {
                    changed = false;
                }

                if(changed) {
                    return "white"
                }

                return cellInActive;
            },
            onFocus: (row: PriceLine2, c: Column<PriceLine2>, anchor: any) => {
                if(!changeset) return null;

                let changed = false;

                if("isChangedSinceBackup" in c && c.isChangedSinceBackup) {
                    changed = c.isChangedSinceBackup(row)
                } else if(c.editKey) {
                    changed = !!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)
                } else {
                    changed = false;
                }

                if(changed) {
                    const value = row.backup as any;
                    return (
                        <PreviousValuePopup anchor={anchor} width={c.width} value={c.render(value, c)} />
                    )
                }

                return null;
            }
        }
    }, [changeset]);

    return {
        markup,
        Loading: markupReq.LoadingOrErrorElement || ctx.loading ? <LinearProgress /> : null,
        cellCustomize,
        sessionId: previewChangeset.sessionId || ctx.sessionId || undefined,
        isPreview: previewChangeset.enabled,
    }
}

export function commonPricingColumns3Start() {
    return [
        ViewOnly(StringCol<PriceLine2>("Opening", "openingName", 150)),
        ViewOnly(StringCol<PriceLine2>("Floor", "floor", 150)),
        ViewOnly(StringCol<PriceLine2>("Location", "locationOne", 150)),
        ViewOnly(StringCol<PriceLine2>("To/From", "locationTransition", 150)),
        ViewOnly(StringCol<PriceLine2>("Location 2", "locationTwo", 150)),
        ViewOnly(Checkbox2Col("Ext", "exterior", {disabled: (o) => true}) as any),
    ]
}

export function commonRefundColumns3() {
    return [
        {
            name: "Status",
            render: o => <CellWithBg backgroundColor={red["100"]}>Refund</CellWithBg>
        },
        NumberCol("Qty", "qty"),
        AdjustCol(NumberCol("Keep %", "refundKeepPercent"), {
            render: (dt: PriceLine2) => dt.refundKeepPercent + "%",
        }),
        CheckboxCol("Full Ref.", "refundFullRefund"),
        CentsCol("Refund", "refundAmount"),
        ViewOnly(CentsCol("Cost (ea)", "unitCostCents")),
        RenderStringCol<PriceLine2>("Markup", dt => {
            if(!isNullOrUndefined(dt.markupPercent)) return dt.markupPercent + "%"
            if(!!dt.markupCents) return "$" + formatCents(dt.markupCents)
            return "auto"
        }),
        ViewOnly(CentsCol("Price", "extendedPrice")),
    ]
}

export function commonPricingColumns3(u: User | null, defaultMarkup: string, sov: number| null = null, forSidePanel: boolean = false) {

    if(sov) {
        return [
            ViewOnly(CentsCol("Price", "extendedPriceCentsAwarded")),
        ]
    }

    return [
        forSidePanel ? null : StatusCol("Status",  "inventoryStatus"),
        NumberCol("Qty", "qty"),
        AdjustCol(CentsCol(costEachColumnName, "unitCostCents", 80), {
            showHintOnFocus: forSidePanel ? undefined : SidePricingPanelOnFocus,
        }),
        {
            name: markupColumnName,
            isChangedSinceBackup: (row: PriceLine2) => {
                if(!row.backup) return false;
                return row.markupCents !== row.backup?.markupCents || row.markupPercent !== row.backup?.markupPercent
            },
            editable: {
                type: "custom",
                copy: (dt: PriceLine2) => {
                    if(!isNullOrUndefined(dt.markupPercent)) return dt.markupPercent + "%"
                    if(!!dt.markupCents) return "$" + formatCents(dt.markupCents)
                    return "auto"
                },
                paste: (rw: PriceLine2, value: string) => {
                    Object.assign(rw, parseMarkup(value))
                },
                render: MarkupEditCell as any,

            },
            showHintOnFocus: forSidePanel ? undefined : SidePricingPanelOnFocus,
            sort: (a: PriceLine2, b: PriceLine2) => sortStringInner(renderMarkup(a), renderMarkup(b)),
            render: (dt: PriceLine2) => {
                return renderMarkup(dt)
            },
            alignRight: true,
            width: 80,
        }, {
            name: "Unit Price",
            render: (dt: PriceLine2) => formatCents(renderUnitPrice(dt)),
            sort: (a: PriceLine2, b: PriceLine2) => sortNumericInner(renderUnitPrice(a), renderUnitPrice(b)),
            alignRight: true,
            width: 80,
        }, {
            name: "Price",
            editable: {
                type: "number",
                parseValue: centsParseValue,
            },
            showHintOnFocus: forSidePanel ? undefined : SidePricingPanelOnFocus,
            alignRight: true,
            editKey: "extendedPrice",
            render: (dt: PriceRider) => formatCents(dt.extendedPrice),
            copy: (dt: PriceRider) => formatCents(dt.extendedPrice),
            sort: (a: PriceLine2, b: PriceLine2) => sortNumericInner(a.extendedPrice, b.extendedPrice),
        },
        hasPermission(u, "CanViewProjectPricingProfit") && {
            name: "Proj. Profit",
            render: (dt: PriceLine2) => formatCents(profit(dt)),
            alignRight: true,
            width: 90,
            sort: (a: PriceLine2, b: PriceLine2) => sortNumericInner(profit(a), profit(b)),
        },
        hasPermission(u, "CanViewProjectPricingProfit") && {
            name: "Act. Profit",
            render: (dt: PriceLine2) => {
                const act = getActualProfit(dt)
                if(act === null) return ""
                return formatCents(act)
            },
            sort: (a: PriceLine2, b: PriceLine2) => sortNumericInner(getActualProfit(a) || 0, getActualProfit(b) || 0),
            alignRight: true,
            width: 90,
        },
        {
            name: "Proj. Margin",
            render: (dt: PriceLine2) => {
                return getMargin(dt)
            },
            alignRight: true,
            width: 100,
        },
        {
            name: "Suggested Markup",
            render: (dt: PriceLine2) => {
                return defaultMarkup;
            },
            alignRight: true,
            width: 150,
        },
        forSidePanel ? null : AdjustCol(RenderStringCol("Refund", (dt: PriceLine2) => {
            if(!dt.refunded) return "";
            return formatCents(dt.refundAmount);
        }), {
            alignRight: true,
        }),
        forSidePanel ? StatusCol("Status",  "inventoryStatus") : null,
    ]
}

export function getMargin(dt: PriceLine2) {
    let profit = dt.extendedPrice - dt.unitCostCents * dt.qty;
    if(profit === 0) return 0
    return Math.round(profit / dt.extendedPrice * 100);
}

export function getActualProfit(dt: PriceLine2) {
    if(isNullOrUndefined(dt.revenueDelivered) || isNullOrUndefined(dt.costDelivered)) return null;
    return dt.revenueDelivered - dt.costDelivered;
}

export function renderMarkup(dt: PriceLine2) {
    if(!isNullOrUndefined(dt.markupPercent)) return dt.markupPercent + "%"
    if(!!dt.markupCents) return "$" + formatCents(dt.markupCents)
    return "auto"
}

export function renderUnitPrice(dt: PriceLine2) {
    return dt.extendedPrice / fixZero(dt.qty)
}

function fixZero(qty: number) {
    if(qty === 0) return 1;
    return qty;
}

export function profit(dt: PriceLine2 | MiscPrice) {
    const qty = "qty" in dt ? dt.qty : 1;
    return dt.extendedPrice - dt.unitCostCents * qty
}

export function sovColumns(sov: number | null | undefined) {
    if(!sov) return [];

    return [
        CheckboxExtSelectorCol(),
        RenderStringCol<PriceLine2 | PriceRider>("SOV", s => {
            if(!!s.sovHeading) {
                return s.sovHeadingName || ""
            }

            return "Unassigned"
        })
    ]
}

export function sovCellCustomize(sov: number) {
    return {
        backgroundColor: d => {
            if(d.sovHeading && d.sovHeading !== sov) return grey["200"];
            if(d.sovHeading === sov) return green["50"];
        },
    }
}